import { MultiSelect, Select } from '@thanx/uikit/select'
import { useStyletron } from '@thanx/uikit/theme'
import { AmountType, OrderHandoff } from 'api/serviceCharges'
import LocationMultiSelect, {
  LocationMultiSelectOption,
} from 'components/LocationMultiSelect'
import SearchInput from 'components/SearchInput'
import { buildTranslate } from 'locales'
import React, { useMemo } from 'react'

const t = buildTranslate('locations.location_list.service_charge_groups')

type Option = { value: string; label: string }

const TYPE_VALUES: AmountType[] = ['FIXED', 'PERCENT', 'OPEN']
const HANDOFF_VALUES: OrderHandoff[] = [
  'UNKNOWN',
  'PICKUP',
  'DELIVERY',
  'CURBSIDE',
  'TABLESIDE',
  'DRIVETHRU',
  'DINEIN',
]

export type ServiceChargeGroupFilters = {
  types: AmountType[]
  enabled: boolean | undefined
  handoffs: OrderHandoff[]
  locationIds: number[]
}

type Props = {
  searchValue: string
  onSearchChange: (value: string) => void
  filters: ServiceChargeGroupFilters
  onTypesChange: (types: AmountType[]) => void
  onEnabledChange: (enabled: boolean | undefined) => void
  onHandoffsChange: (handoffs: OrderHandoff[]) => void
  locations: LocationMultiSelectOption[]
  onToggleLocation: (id: number) => void
}

/**
 * The group list's five filter axes (search, types[], enabled, handoffs[],
 * location_ids[]) plus their controlled state. The scene owns the actual
 * request params (including search debouncing) — this component only
 * renders the controls and reports raw changes upward.
 */
const FilterBar = ({
  searchValue,
  onSearchChange,
  filters,
  onTypesChange,
  onEnabledChange,
  onHandoffsChange,
  locations,
  onToggleLocation,
}: Props) => {
  const [css] = useStyletron()

  const typeOptions: Option[] = useMemo(
    () =>
      TYPE_VALUES.map(value => ({
        value,
        label: t(`filters.type_options.${value.toLowerCase()}`) as string,
      })),
    []
  )
  const selectedTypeOptions = typeOptions.filter(o =>
    filters.types.includes(o.value as AmountType)
  )

  const handoffOptions: Option[] = useMemo(
    () =>
      HANDOFF_VALUES.map(value => ({
        value,
        label: t(`filters.handoff_options.${value.toLowerCase()}`) as string,
      })),
    []
  )
  const selectedHandoffOptions = handoffOptions.filter(o =>
    filters.handoffs.includes(o.value as OrderHandoff)
  )

  const enabledOptions: Option[] = useMemo(
    () => [
      { value: 'true', label: t('filters.enabled_options.enabled') as string },
      {
        value: 'false',
        label: t('filters.enabled_options.disabled') as string,
      },
    ],
    []
  )
  const selectedEnabledOption = enabledOptions.find(
    o => o.value === String(filters.enabled)
  )

  return (
    <div className="mb-m">
      <div className="mb-m">
        <SearchInput
          value={searchValue}
          onChange={onSearchChange}
          ariaLabel={t('filters.search_aria') as string}
          placeholder={t('filters.search_placeholder') as string}
        />
      </div>
      <div className={`d-flex flex-wrap ${css({ gap: '12px' })}`}>
        <div className={css({ width: '220px' })}>
          <MultiSelect
            aria-label={t('filters.type_label')}
            placeholder={t('filters.type_placeholder')}
            multi
            options={typeOptions}
            value={selectedTypeOptions}
            onChange={selected =>
              onTypesChange(selected.map(o => o.value as AmountType))
            }
          />
        </div>
        <div className={css({ width: '180px' })}>
          {/* Same Select family (Base Web via @thanx/uikit/select) as its
              MultiSelect siblings, so the row's control heights match. */}
          <Select
            aria-label={t('filters.enabled_label')}
            placeholder={t('filters.enabled_placeholder')}
            options={enabledOptions}
            value={selectedEnabledOption ? [selectedEnabledOption] : []}
            onChange={selected => {
              const next = selected[0]?.value
              onEnabledChange(next === undefined ? undefined : next === 'true')
            }}
            // The uikit Select wrapper defaults clearable to false — this
            // filter needs a way back to the unset ("All") tri-state.
            clearable
          />
        </div>
        <div className={css({ width: '220px' })}>
          <MultiSelect
            aria-label={t('filters.handoff_label')}
            placeholder={t('filters.handoff_placeholder')}
            multi
            options={handoffOptions}
            value={selectedHandoffOptions}
            onChange={selected =>
              onHandoffsChange(selected.map(o => o.value as OrderHandoff))
            }
          />
        </div>
        <div className={css({ width: '260px', marginTop: '-8px' })}>
          <LocationMultiSelect
            locations={locations}
            selectedIds={filters.locationIds}
            onToggle={onToggleLocation}
            idPrefix="service-charge-groups-locations"
            labels={{
              selectLocations: t(
                'filters.location_multi_select.select_locations'
              ) as string,
              locationsSelected: t(
                filters.locationIds.length === 1
                  ? 'filters.location_multi_select.locations_selected_one'
                  : 'filters.location_multi_select.locations_selected_other',
                { count: filters.locationIds.length }
              ) as string,
              noLocationsAvailable: t(
                'filters.location_multi_select.no_locations_available'
              ) as string,
              searchLocations: t(
                'filters.location_multi_select.search_locations'
              ) as string,
              noLocationsMatch: t(
                'filters.location_multi_select.no_locations_match'
              ) as string,
            }}
          />
        </div>
      </div>
    </div>
  )
}

export default FilterBar
